import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Video, Mic, MicOff, VideoOff, Phone, Users, MessageSquare, Settings, Monitor } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_talentmeet-app/artifacts/w1exeb5m_IMG-20260127-WA0008.jpg";

// Sample image for video call mockup
const SAMPLE_PARTICIPANT = "https://images.unsplash.com/photo-1576987212553-c43b8e44bb1f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwyfHxhY3RvciUyMGhlYWRzaG90JTIwcHJvZmVzc2lvbmFsJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzY5ODU3NDAyfDA&ixlib=rb-4.1.0&q=85";

export default function AuditionRoom() {
  const navigate = useNavigate();
  const [isMuted, setIsMuted] = useState(false);
  const [isVideoOn, setIsVideoOn] = useState(true);

  return (
    <div className="min-h-screen bg-gray-900">
      {/* Header */}
      <header className="bg-gray-800 border-b border-gray-700 px-4 py-3">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="text-white hover:bg-gray-700">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <img src={LOGO_URL} alt="TalentMeet" className="h-8 w-8 rounded-lg" />
              <span className="font-display text-xl font-bold text-white">Audition Room</span>
            </div>
          </div>
          <Badge className="bg-teal-500 text-white">
            <span className="w-2 h-2 bg-white rounded-full mr-2 animate-pulse"></span>
            Live Session
          </Badge>
        </div>
      </header>

      {/* Main Video Area */}
      <main className="max-w-7xl mx-auto p-4">
        <div className="grid lg:grid-cols-4 gap-4">
          {/* Main Video */}
          <div className="lg:col-span-3">
            <Card className="bg-gray-800 border-gray-700 overflow-hidden">
              <CardContent className="p-0 relative">
                <div className="relative aspect-video bg-gray-900">
                  <img 
                    src={SAMPLE_PARTICIPANT}
                    alt="Casting Director"
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute bottom-4 left-4 bg-black/50 backdrop-blur-sm rounded-lg px-3 py-1">
                    <span className="text-white text-sm font-medium">Casting Director - John Smith</span>
                  </div>
                  
                  {/* Self View */}
                  <div className="absolute bottom-4 right-4 w-48 h-36 bg-gray-800 rounded-lg overflow-hidden border-2 border-gray-600">
                    {isVideoOn ? (
                      <div className="w-full h-full bg-gradient-to-br from-violet-600 to-pink-600 flex items-center justify-center">
                        <span className="text-white text-3xl font-bold">You</span>
                      </div>
                    ) : (
                      <div className="w-full h-full bg-gray-700 flex items-center justify-center">
                        <VideoOff className="w-8 h-8 text-gray-400" />
                      </div>
                    )}
                  </div>
                </div>

                {/* Controls */}
                <div className="absolute bottom-0 left-0 right-0 p-4 bg-gradient-to-t from-black/80 to-transparent">
                  <div className="flex items-center justify-center gap-4">
                    <Button
                      variant={isMuted ? "destructive" : "secondary"}
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => setIsMuted(!isMuted)}
                      data-testid="mute-btn"
                    >
                      {isMuted ? <MicOff className="w-6 h-6" /> : <Mic className="w-6 h-6" />}
                    </Button>
                    <Button
                      variant={isVideoOn ? "secondary" : "destructive"}
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => setIsVideoOn(!isVideoOn)}
                      data-testid="video-btn"
                    >
                      {isVideoOn ? <Video className="w-6 h-6" /> : <VideoOff className="w-6 h-6" />}
                    </Button>
                    <Button
                      variant="secondary"
                      size="lg"
                      className="rounded-full w-14 h-14"
                      data-testid="screen-share-btn"
                    >
                      <Monitor className="w-6 h-6" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="lg"
                      className="rounded-full w-14 h-14"
                      onClick={() => navigate("/talent/dashboard")}
                      data-testid="end-call-btn"
                    >
                      <Phone className="w-6 h-6 rotate-[135deg]" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Sidebar */}
          <div className="space-y-4">
            {/* Participants */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <Users className="w-5 h-5" /> Participants (3)
                </h3>
                <div className="space-y-3">
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-violet-500 flex items-center justify-center">
                      <span className="text-white font-bold">JS</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">John Smith</p>
                      <p className="text-gray-400 text-xs">Casting Director</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-pink-500 flex items-center justify-center">
                      <span className="text-white font-bold">AM</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">Anna Miller</p>
                      <p className="text-gray-400 text-xs">Producer</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-teal-500 flex items-center justify-center">
                      <span className="text-white font-bold">Y</span>
                    </div>
                    <div>
                      <p className="text-white text-sm font-medium">You</p>
                      <p className="text-gray-400 text-xs">Talent</p>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Chat Placeholder */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold flex items-center gap-2 mb-4">
                  <MessageSquare className="w-5 h-5" /> Chat
                </h3>
                <div className="h-48 flex items-center justify-center text-gray-500 text-sm">
                  <p>Chat messages will appear here</p>
                </div>
                <div className="mt-4 flex gap-2">
                  <input
                    type="text"
                    placeholder="Type a message..."
                    className="flex-1 bg-gray-700 border-gray-600 rounded-lg px-3 py-2 text-white text-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-violet-500"
                    disabled
                  />
                  <Button variant="secondary" size="sm" disabled>
                    Send
                  </Button>
                </div>
              </CardContent>
            </Card>

            {/* Audition Info */}
            <Card className="bg-gray-800 border-gray-700">
              <CardContent className="p-4">
                <h3 className="text-white font-semibold mb-3">Audition Details</h3>
                <div className="text-gray-400 text-sm space-y-2">
                  <p><strong className="text-gray-300">Project:</strong> Summer Film 2024</p>
                  <p><strong className="text-gray-300">Role:</strong> Lead Character</p>
                  <p><strong className="text-gray-300">Duration:</strong> ~30 min</p>
                </div>
                <Badge className="mt-3 bg-violet-500/20 text-violet-300 border border-violet-500/30">
                  Demo Mode - Mocked UI
                </Badge>
              </CardContent>
            </Card>
          </div>
        </div>
      </main>
    </div>
  );
}
